import { AlarmTableInModal } from '@/components/alarms';
import { Modal } from '@/components/common';
import { buildColumn, ColumnsProps, ScrollType } from '@/Helper';
import {
	AcknowledgedAndClearAlarmsPayload,
	AlarmAcknowledgementActions,
	AlarmActionOptions,
	AlarmDetailsExpandedState,
	AlarmModel,
	AlarmNote,
	AlarmNoteForAcknowledge,
	AlarmState,
} from '@/model/AlarmModel';
import { BaseColumns } from '@/model/CommonModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { setAlarmNoteForAcknowledge, setShowAlarmAddOperatorNote, setShowAlarmCredentials } from '@/store/alarm/actions';
import { selectAlarmDetailsForModal, selectOperatorNotesAdded } from '@/store/alarm/selectors';
import { CaretRightOutlined, ReadOutlined } from '@ant-design/icons';
import { Collapse, CollapseProps, notification, Spin, Table, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import {
	acknowledgeAndClearAlarmCommon,
	acknowledgedAndClearAlarms,
	getAlarmNoteAddOperatorNoteModal,
	getMultipleAlarmAction,
	instructionsStyle,
	notesStyle,
} from '../helpers';
import { IndividualActionButtons } from '../IndividualActionButtons/IndividualActionButtons';
import styles from './alarmDetailsModal.module.scss';

const scroll: ScrollType = { x: 200, y: '47vh' };

type Props = {
	onHideModal: () => void;
};

type AlarmNotesDataSourceType = AlarmNote & BaseColumns;

const { Paragraph } = Typography;

const AlarmDetailsModal: React.FC<Props> = ({ onHideModal }) => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [expandedKeys, setExpandedKeys] = useState<Array<string | number> | string | number>([]);

	const payload = useStoreSelector<{
		alarmModel: Partial<AlarmModel>;
		showAlarmDetailsExpandedState: AlarmDetailsExpandedState;
	}>(selectAlarmDetailsForModal);
	const alarmModel = payload.alarmModel as AlarmModel;
	const showAlarmDetailsExpandedState = payload.showAlarmDetailsExpandedState;

	//we need this empty selector in order to reflect the changes in the component for notes
	const operatorNotesAdded: AlarmNote[] = useStoreSelector<AlarmNote[]>(selectOperatorNotesAdded);

	useEffect(() => {
		if (alarmModel) {
			batch(() => {
				const expandedKeys: Array<string | number> | string | number = [];
				switch (showAlarmDetailsExpandedState) {
					case AlarmDetailsExpandedState.Both: {
						if (alarmModel.Instructions.length) {
							expandedKeys.push(1);
						}
						if (alarmModel.AlarmDetails[0].AlarmNotes.length) {
							expandedKeys.push(2);
						}
						break;
					}

					case AlarmDetailsExpandedState.Instructions: {
						expandedKeys.push(1);
						break;
					}

					case AlarmDetailsExpandedState.Notes: {
						expandedKeys.push(2);
						break;
					}
				}

				setExpandedKeys(expandedKeys);
				setIsLoading(false);
			});
		} else {
			onHideModal();
		}
	}, []);

	const handleActionDropdownCallback = useCallback(async (option: AlarmAcknowledgementActions | AlarmActionOptions) => {
		handleActionDropdown(option);
	}, []);
	//#endregion

	const addOperatorNoteCallback = (action: AlarmAcknowledgementActions, payload: AcknowledgedAndClearAlarmsPayload) => {
		notification['success']({
			message: 'Operator Note Added',
		});
		return getAlarmNoteAddOperatorNoteModal(action, true, payload.alarmIds, [alarmModel], payload.alarmsDetails);
	};

	const setAlarmNoteForAcknowledgeCallback = (alarmNote: AlarmNoteForAcknowledge) => {
		dispatch(setAlarmNoteForAcknowledge(alarmNote));
	};

	const setShowAlarmAddOperatorNoteCallback = () => {
		dispatch(setShowAlarmAddOperatorNote(true));
	};

	const openAddOperatorNoteModal = (action: AlarmAcknowledgementActions, isAlarmNoteForAcknowledge: boolean, alarmIds: React.Key[]) => {
		const alarmNote = getAlarmNoteAddOperatorNoteModal(action, isAlarmNoteForAcknowledge, alarmIds, [alarmModel]);
		batch(() => {
			dispatch(setShowAlarmAddOperatorNote(true));
			dispatch(setAlarmNoteForAcknowledge(alarmNote));
		});
	};

	const handleActionDropdown = async (option: AlarmAcknowledgementActions | AlarmActionOptions) => {
		let payload: AcknowledgedAndClearAlarmsPayload = {
			action: option as AlarmAcknowledgementActions,
			operatorNotes: [],
			alarmIds: alarmModel.AlarmDetails.map(x => x.ActiveAlarmId),
			saveChangesCallback: () => {
				switch (option) {
					case AlarmAcknowledgementActions.AcknowledgedOne: {
						notification['success']({
							message: 'Alarm Acknowledged',
						});
						break;
					}

					case AlarmAcknowledgementActions.ClearOneAcknowledged:
					case AlarmAcknowledgementActions.ClearOneUnacknowledged: {
						notification['success']({
							message: 'Alarm Cleared',
						});
						break;
					}
				}
				onHideModal();
			},
			acknowledgeAndClearCallback: acknowledgeAndClearAlarmCommon,
			addOperatorNoteCallback,
			setAlarmNoteForAcknowledgeCallback,
			setShowAlarmAddOperatorNoteCallback,
		};

		switch (option) {
			case AlarmAcknowledgementActions.AcknowledgedOne:
			case AlarmAcknowledgementActions.ClearOneAcknowledged:
			case AlarmAcknowledgementActions.ClearOneUnacknowledged: {
				payload.action = getMultipleAlarmAction(payload.alarmIds, option);
				acknowledgedAndClearAlarms(payload);
				break;
			}

			case AlarmActionOptions.ADD_OPERATOR_NOTE: {
				const action = AlarmAcknowledgementActions.None;
				openAddOperatorNoteModal(action, false, payload.alarmIds);
				break;
			}

			case AlarmActionOptions.DISPLAY_CREDENTIALS: {
				const credentialsIds = [];
				alarmModel?.AlarmDetails?.forEach(detail => {
					if (detail.UID1 > 0) {
						credentialsIds.push(detail.UID1);
					}

					if (detail.UID2 > 0) {
						credentialsIds.push(detail.UID2);
					}
				});

				if (credentialsIds.length) {
					dispatch(
						setShowAlarmCredentials({
							showCredentials: true,
							credentialsIds,
						})
					);
				}
				break;
			}
		}
	};

	const columns: ColumnsProps<AlarmNotesDataSourceType>[] = [
		{
			...buildColumn(_('Date'), 'Date', '13em', 'start'),
			render: value => {
				let text = value;
				try {
					text = FormatDate(text, true, null, true);
				} catch {
					text = value;
				}

				return <span>{text}</span>;
			},
		},
		{
			...buildColumn(_('Operator'), 'OperatorName', 'auto', 'start'),
		},
	];

	let dataSource: AlarmNotesDataSourceType[] = undefined;
	if (alarmModel) {
		const alarmNotes: AlarmNotesDataSourceType[] = alarmModel.AlarmDetails[0].AlarmNotes.map<AlarmNotesDataSourceType>((note: AlarmNote) => ({
			...note,
			key: note.AlarmNoteId,
		}));
		dataSource = alarmNotes.sort((a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime());
	}

	const onChange = (keys: string[]) => {
		setExpandedKeys(keys);
	};

	const items: CollapseProps['items'] = [];
	if (alarmModel?.Instructions?.length) {
		items.push({
			key: 1,
			label: (
				<div className={styles.accordeonTitle}>
					<span>
						<ReadOutlined style={instructionsStyle} />
						<span style={{ ...instructionsStyle, fontSize: '14px' }}>&nbsp;&nbsp;{_('Instructions')}</span>
					</span>
					<CaretRightOutlined />
				</div>
			),
			children: (
				<div className={styles.instructions}>
					<Paragraph>{alarmModel?.Instructions}</Paragraph>
				</div>
			),
			showArrow: false,
		});
	}

	if (dataSource?.length) {
		items.push({
			key: 2,
			label: (
				<div className={styles.accordeonTitle}>
					<span>
						<ReadOutlined style={notesStyle} />
						<span style={{ ...notesStyle, fontSize: '14px' }}>&nbsp;&nbsp;{_('OperatorNotes')}</span>
					</span>
					<CaretRightOutlined />
				</div>
			),
			children: (
				<div className={styles.accordeonTitle}>
					<Table
						size='small'
						columns={columns}
						dataSource={dataSource}
						className={styles.alarmInfo}
						pagination={false}
						expandable={{
							expandedRowRender: (record: AlarmNotesDataSourceType) => (
								<div id={`alarmInfoNote-${record.AlarmNoteId}`} className={styles.instructions}>
									<Paragraph>{record.Text}</Paragraph>
								</div>
							),
							expandedRowClassName: () => styles.expandedRow,
						}}
						scroll={scroll}
					/>
				</div>
			),
			showArrow: false,
		});
	}

	let hasCredentialId = alarmModel?.AlarmDetails?.filter(detail => detail.UID1 > 0 || detail.UID2 > 0).length > 0;

	return (
		<Modal onCancel={onHideModal} onClickOk={() => null} width={750} title='Alarm Information' visible={true} customZoomClass={styles.modal} footer={null}>
			<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
				<div className={styles.content}>
					<div>
						<AlarmTableInModal />
						<IndividualActionButtons
							showPreviewBadge={hasCredentialId}
							handleAction={handleActionDropdownCallback}
							isAcknowledgedAlarm={alarmModel?.State === AlarmState.Acknowledged}
							isAlarmSelected={true}
						/>
					</div>
					{items?.length > 0 && (
						<div>
							<Collapse items={items} activeKey={expandedKeys} onChange={onChange} className={styles.collapse} />
						</div>
					)}
				</div>
			</Spin>
		</Modal>
	);
};

export { AlarmDetailsModal };
